import { useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/motion";
import { JourneyTimeline } from "@/components/JourneyTimeline";
import { CtaBand } from "@/components/CtaBand";
import { Reveal } from "@/components/motion";
import { usePageMeta } from "@/lib/seo";
import { LeafMark } from "@/components/LeafMark";

const JourneyPathways = () => {
    const { t, tHtml, site: siteData } = useLang();
    const { JOURNEY_PATHWAYS } = siteData;
    const [active, setActive] = useState("medical");
    const pathway = JOURNEY_PATHWAYS.find((p) => p.id === active);
    return (
        <section className="section-pad pt-10" data-testid="journey-pathways">
            <div className="container-site">
                <SectionHeading
                    index="—"
                    eyebrow={t("your_journey.eyebrow", "Three Pathways")}
                    title={tHtml(
                        "your_journey.pathwaysTitle",
                        <>
                            Medical, Wellness or{" "}
                            <span className="italic text-pine">Ayurveda.</span>
                        </>
                    )}
                    intro={t("your_journey.intro", "Every journey follows its own rhythm — choose a pathway to see the steps.")}
                />
                <Reveal delay={0.05}>
                    <div
                        className="mt-10 inline-flex flex-wrap gap-2 rounded-xl border border-pine/10 bg-white p-1.5"
                        role="tablist"
                        aria-label="Journey pathways"
                        data-testid="pathway-tabs"
                    >
                        {JOURNEY_PATHWAYS.map((p) => (
                            <button
                                key={p.id}
                                type="button"
                                role="tab"
                                aria-selected={active === p.id}
                                data-testid={`pathway-tab-${p.id}`}
                                onClick={() => setActive(p.id)}
                                className={`rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                                    active === p.id
                                        ? "bg-forest text-ivory"
                                        : "text-moss hover:text-forest"
                                }`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <div
                        key={pathway.id}
                        data-testid={pathway.testid}
                        className="mt-8 bg-forest text-ivory rounded-2xl p-8 sm:p-12"
                    >
                        <p className="eyebrow text-natural text-[11px]">{pathway.tagline}</p>
                        <ol className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-8">
                            {pathway.steps.map((step, i) => (
                                <li key={step} data-testid={`${pathway.testid}-step-${i + 1}`} className="relative">
                                    <span className="flex items-center gap-3">
                                        <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-ivory/10 border border-natural/40 font-serif italic text-natural">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        {i < pathway.steps.length - 1 && (
                                            <span aria-hidden="true" className="hidden lg:block flex-1 h-px bg-natural/25" />
                                        )}
                                    </span>
                                    <p className="mt-4 text-sm font-semibold leading-snug">{step}</p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export const YourJourney = () => {
    const { t, site: siteData } = useLang();
    const { COORDINATION_GROUPS } = siteData;
    usePageMeta(
        t("meta.your_journey.title", "Your Journey"),
        t("meta.your_journey.desc", "Your journey with UniversHeal — three pathways: medical care, wellness and Ayurveda. Each stage coordinated with you, from enquiry in your country to your return home.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("your_journey.eyebrow", "Your Journey")}
                title={
                    <>
                        From your country to Kerala —{" "}
                        <span className="italic text-pine">and back home.</span>
                    </>
                }
                intro={t("your_journey.intro", "Three pathways, one team coordinating them with you. You always know where you are in the journey and what happens next.")}
            />

            <JourneyPathways />

            <section className="section-pad pt-0">
                <div className="container-site max-w-5xl">
                    <SectionHeading
                        index="—"
                        eyebrow={t("your_journey.eyebrow", "The Medical Pathway")}
                        title={t("your_journey.medicalTitle", "The medical journey, stage by stage.")}
                        intro={t("your_journey.intro", "Seven clear stages for treatment and specialist care — scroll the path your care will take.")}
                    />
                    <div className="mt-14">
                        <JourneyTimeline />
                    </div>
                </div>
            </section>

            <section className="section-pad bg-sage/60">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("your_journey.eyebrow", "Along The Way")}
                        title={t("your_journey.coordinateTitle", "What we coordinate at each stage.")}
                        intro={t("your_journey.intro", "Where a service is delivered by trusted local partners, we coordinate it on your behalf — so you always have a single point of contact.")}
                    />
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {COORDINATION_GROUPS.map((group, i) => (
                            <Reveal key={group.title} delay={i * 0.07} className="h-full">
                                <div
                                    data-testid={`journey-coordination-${i + 1}`}
                                    className="h-full bg-white border border-pine/10 rounded-2xl p-8"
                                >
                                    <p className="eyebrow text-forest text-[11px]">
                                        {group.title}
                                    </p>
                                    <ul className="mt-6 space-y-3">
                                        {group.items.map((item) => (
                                            <li
                                                key={item}
                                                className="text-sm text-charcoal/80 leading-snug"
                                            >
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <CtaBand
                headline={t("your_journey.headline", "Stage one starts with a message.")}
                text={t("your_journey.text", "Share your case — the rest of the journey unfolds from there, coordinated with you at every step.")}
            />
        </>
    );
};
